import { AudienceTypeDisplayNames } from "models/enumerations/audiences/audience-type";
import { Course } from "models/interfaces/courses/course";
import { CourseVersionRecord } from "./course-version-record";
import { DateUtils } from "utilities/date-utils";
import { Record } from "immutable";
import { RecordUtils } from "andculturecode-javascript-core";
import { StringUtils } from "utilities/string-utils";
import { UserRecord } from "models/view-models/users/user-record";
import { t } from "utilities/localization/t";

// -------------------------------------------------------------------------------------------------
// #region Default Values
// -------------------------------------------------------------------------------------------------

const defaultValues: Course = {
    activatedById: undefined,
    activatedOn: undefined,
    archivedById: undefined,
    archivedOn: undefined,
    audienceType: 0,
    changeLogDescription: "",
    createdBy: undefined,
    createdById: undefined,
    createdOn: undefined,
    deactivatedById: undefined,
    deactivatedOn: undefined,
    deletedById: undefined,
    deletedOn: undefined,
    description: undefined,
    id: undefined,
    hasNoContent: false,
    language: undefined,
    latestCourseVersion: undefined,
    latestVersionId: 0,
    latestCourseVersionId: 0,
    latestExternalScormCourseId: undefined,
    name: "",
    status: undefined,
    topic: undefined,
    updatedBy: undefined,
    updatedById: undefined,
    updatedOn: undefined,
};

// #endregion Default Values

// -------------------------------------------------------------------------------------------------
// #region Record
// -------------------------------------------------------------------------------------------------

class CourseRecord extends Record(defaultValues) implements Course {
    // -------------------------------------------------------------------------------------------------
    // #region Constructor
    // -------------------------------------------------------------------------------------------------

    constructor(params?: Partial<Course>) {
        params = params ?? Object.assign({}, defaultValues);

        if (params.createdBy != null) {
            params.createdBy = RecordUtils.ensureRecord(params.createdBy, UserRecord);
        }

        if (params.updatedBy != null) {
            params.updatedBy = RecordUtils.ensureRecord(params.updatedBy, UserRecord);
        }

        if (params.latestCourseVersion != null) {
            params.latestCourseVersion = RecordUtils.ensureRecord(
                params.latestCourseVersion,
                CourseVersionRecord
            );
        }

        super(params);
    }

    // #endregion Constructor

    // -------------------------------------------------------------------------------------------------
    // #region Public Methods
    // -------------------------------------------------------------------------------------------------

    /**
     * Convenience method to determine if the details on this record are complete.
     *
     * @returns {boolean} Whether or not the details are complete
     */
    public detailsAreComplete(): boolean {
        return (
            StringUtils.hasValue(this.description) && this.language != null && this.topic != null
        );
    }

    /**
     * Convenience method to return the date/time this record was created and the name of the user
     * who created it.
     *
     * @returns {string} A string indicating when this record was created and by whom.
     */
    public getCreatedText(): string {
        const createdOnDateTime = DateUtils.formatDateTime(false, this.createdOn);

        if (this.createdBy == null) {
            return createdOnDateTime;
        }

        return t("createdondatetimeByThisCreatedByGetFirstAndLastName", {
            createdOnDateTime: createdOnDateTime,
            thisCreatedByGetFirstAndLastName: this.createdBy.getFirstAndLastName(),
        });
    }

    /**
     * Convenience method to the return audience type Enum string with spaces between each word.
     * Ex: EducationNetwork -> Authorized Education Network
     */
    public getAudienceTypeWithSpaces(): string {
        if (this == null || this.audienceType == null) {
            return "";
        }

        return t(AudienceTypeDisplayNames[this.audienceType]);
    }

    /**
     * Convenience method to the return user
     * Ex: Steve Smith
     */
    public getLastUpdatedBy(): string {
        const updatedBy = this.updatedBy ?? this.createdBy;

        if (updatedBy == null) {
            return "N/A";
        }

        return updatedBy.getFirstAndLastName();
    }

    /**
     * Convenience method to the return the text for the last updated date text
     * Ex: on YYYY/MM/D at 8:00 am
     */
    public getLastUpdatedDateAndTime(): string {
        const message = DateUtils.formatLastEditedDateTime(this, true, true);

        return message;
    }

    /**
     * Convenience method to the return the text for the last updated date text along with the user
     * Ex: on YYYY/MM/D at 8:00 AM by User
     */
    public getLastUpdatedText(): string {
        const message = DateUtils.formatLastEditedDate(this, false);
        const updatedBy = this.updatedBy ?? this.createdBy;

        if (updatedBy == null) {
            return message;
        }

        return t("messageByUpdatedByGetFirstAndLastName", {
            message: message,
            updatedByGetFirstAndLastName: updatedBy.getFirstAndLastName(),
        });
    }

    /**
     * Convenience method to the return the text for the last updated date text
     * Ex: on YYYY-MM-DDTHH:MM:SS:FFFFFFF+Offset
     * 2022-10-20T17:42:30.7005991+00:00

     */
    public getLastUpdated(): string {
        return StringUtils.hasValue(this.updatedOn) ? this.updatedOn : this.createdOn ?? "";
    }

    /**
     * Convenience method to the return the last updated time in milliseconds.
     * Ex: 1666287750700 for 2022-10-20T17:42:30.7005991+00:00
     */

    public getLastUpdatedInMilliseconds(): number {
        const lastUpdated = this.getLastUpdated();
        return DateUtils.convertToMilliseconds(lastUpdated);
    }

    /**
     * Convenience method to return the id of the user who last updated the record (or created it,
     * if it has not yet been updated)
     *
     */
    public getUpdatedOrCreatedById(): number | undefined {
        return this.updatedById ?? this.createdById;
    }

    /**
     * Given a set of values for Course properties, create a new CourseRecord object from this
     * instance, overwriting the properties in the values parameter with values provided.
     *
     * @param {Partial<Course>} values The values to overwrite on this instance.
     * @returns A CourseRecord with values from this instance and the values parameter.
     */
    public with(values: Partial<Course>): CourseRecord {
        return new CourseRecord(Object.assign(this.toJS(), values));
    }

    // #endregion Public Methods
}

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { CourseRecord };

// #endregion Exports
